import * as THREE from 'three';

/**
 * Boundary detection utilities for top mesh generation
 * Following the algorithm specified in THICKNESS.md
 */

// Cache expensive topology computations per geometry instance
const __boundaryCache = new WeakMap();

function getEdgeKey(a, b) {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

function getOrBuildBoundaryTopology(geometry) {
  if (!geometry || !geometry.attributes?.position) return null;
  const cached = __boundaryCache.get(geometry);
  if (cached) return cached;

  const posAttr = geometry.attributes.position;
  const vertexCount = posAttr.count;
  const isIndexed = !!geometry.index;
  const indexArray = isIndexed ? geometry.index.array : null;

  // Build triangle index list (supports non-indexed by implicit triples)
  const triangles = [];
  if (isIndexed) {
    for (let i = 0; i < indexArray.length; i += 3) {
      triangles.push([indexArray[i], indexArray[i + 1], indexArray[i + 2]]);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      triangles.push([i, i + 1, i + 2]);
    }
  }

  // Count edge usages and build full vertex adjacency
  const edgeUseCount = new Map();
  const vertexAdjacency = new Map();

  function addAdjacency(a, b) {
    if (!vertexAdjacency.has(a)) vertexAdjacency.set(a, new Set());
    if (!vertexAdjacency.has(b)) vertexAdjacency.set(b, new Set());
    vertexAdjacency.get(a).add(b);
    vertexAdjacency.get(b).add(a);
  }

  for (let t = 0; t < triangles.length; t++) {
    const [a, b, c] = triangles[t];
    const e1 = getEdgeKey(a, b);
    const e2 = getEdgeKey(b, c);
    const e3 = getEdgeKey(c, a);
    edgeUseCount.set(e1, (edgeUseCount.get(e1) || 0) + 1);
    edgeUseCount.set(e2, (edgeUseCount.get(e2) || 0) + 1);
    edgeUseCount.set(e3, (edgeUseCount.get(e3) || 0) + 1);
    addAdjacency(a, b);
    addAdjacency(b, c);
    addAdjacency(c, a);
  }

  // Extract boundary edges and per-vertex boundary adjacency
  const boundaryEdges = [];
  const boundaryAdjacency = new Map();
  const boundaryVerticesSet = new Set();

  function addBoundaryAdj(a, b) {
    if (!boundaryAdjacency.has(a)) boundaryAdjacency.set(a, new Set());
    if (!boundaryAdjacency.has(b)) boundaryAdjacency.set(b, new Set());
    boundaryAdjacency.get(a).add(b);
    boundaryAdjacency.get(b).add(a);
  }

  for (const [key, count] of edgeUseCount.entries()) {
    if (count === 1) {
      const [aStr, bStr] = key.split('-');
      const a = Number(aStr);
      const b = Number(bStr);
      boundaryEdges.push([a, b]);
      boundaryVerticesSet.add(a);
      boundaryVerticesSet.add(b);
      addBoundaryAdj(a, b);
    }
  }

  // Trace boundary loops
  const loops = [];
  const visitedEdges = new Set();

  function visitEdgeKey(a, b) {
    visitedEdges.add(getEdgeKey(a, b));
  }

  function isEdgeVisited(a, b) {
    return visitedEdges.has(getEdgeKey(a, b));
  }

  for (const [a, b] of boundaryEdges) {
    if (isEdgeVisited(a, b)) continue;
    const loop = [a];
    visitEdgeKey(a, b);
    let prev = a;
    let current = b;
    loop.push(current);

    // Walk until we close the loop or cannot continue
    const safetyLimit = vertexCount * 4;
    let steps = 0;
    while (steps++ < safetyLimit) {
      const neighbors = Array.from(boundaryAdjacency.get(current) || []);
      // Pick the neighbor that is not the previous vertex and whose edge is unvisited
      let next = null;
      for (const n of neighbors) {
        if (n === prev) continue;
        if (!isEdgeVisited(current, n)) {
          next = n;
          break;
        }
      }
      if (next == null) {
        // Try to close if neighbor is start
        if (neighbors.includes(loop[0]) && !isEdgeVisited(current, loop[0])) {
          visitEdgeKey(current, loop[0]);
          break;
        }
        break;
      }

      visitEdgeKey(current, next);
      loop.push(next);
      prev = current;
      current = next;
      if (current === loop[0]) break;
    }

    // Ensure loop is meaningful
    if (loop.length >= 3) {
      // Deduplicate potential repeated start at end
      if (loop[loop.length - 1] === loop[0]) loop.pop();
      loops.push(loop);
    }
  }

  const topology = {
    vertexCount,
    edgeUseCount,
    vertexAdjacency,
    boundaryEdges,
    boundaryAdjacency,
    boundaryVerticesSet,
    boundaryLoops: loops
  };

  __boundaryCache.set(geometry, topology);
  return topology;
}

/**
 * Find boundary vertices by checking the top vertices in Y-axis
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} batchSize - Number of vertices to check per batch (default 200)
 * @returns {Array} Array of boundary vertex indices
 */
export function findTopBoundaryVertices(geometry, batchSize = 200) {
  if (!geometry?.attributes?.position) {
    console.warn('Geometry has no position attribute');
    return [];
  }

  const positions = geometry.attributes.position.array;
  const vertexCount = positions.length / 3;
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology) return [];

  // Collect all boundary vertices with their Y, sort descending, return indices
  const boundaryVertices = Array.from(topology.boundaryVerticesSet.values());
  boundaryVertices.sort((a, b) => positions[b * 3 + 1] - positions[a * 3 + 1]);

  // To preserve original intent (batches), we can limit to a batch for extremely large loops
  return boundaryVertices.slice(0, Math.max(batchSize, boundaryVertices.length));
}

/**
 * Check if a vertex is on the boundary of the mesh
 * A vertex is on boundary if any of its connected edges are used by only one face
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} vertexIndex - Index of the vertex to check
 * @param {Array} indices - Face indices array (can be null for non-indexed geometry)
 * @returns {boolean} True if vertex is on boundary
 */
export function isVertexOnBoundary(geometry, vertexIndex, indices) {
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology) return false;
  return topology.boundaryVerticesSet.has(vertexIndex);
}

/**
 * Get all edges connected to a specific vertex
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} vertexIndex - Index of the vertex
 * @param {Array} indices - Face indices array
 * @returns {Array} Array of edges [vertexA, vertexB] connected to the vertex
 */
function getConnectedEdges(geometry, vertexIndex, indices) {
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology) return [];
  const neighbors = Array.from(topology.vertexAdjacency.get(vertexIndex) || []);
  return neighbors.map((n) => [vertexIndex, n]);
}

/**
 * Count how many faces use each edge
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Array} edges - Array of edges to check
 * @param {Array} indices - Face indices array
 * @returns {Map} Map of edge keys to usage count
 */
function countEdgeUsage(geometry, edges, indices) {
  // Kept for backward compatibility; now powered by cached counts
  const topology = getOrBuildBoundaryTopology(geometry);
  const counts = new Map();
  if (!topology) return counts;
  for (const edge of edges) {
    const key = getEdgeKey(edge[0], edge[1]);
    counts.set(key, topology.edgeUseCount.get(key) || 0);
  }
  return counts;
}

/**
 * Create a consistent key for an edge (smaller index first)
 * @param {number} a - First vertex index
 * @param {number} b - Second vertex index
 * @returns {string} Edge key
 */
// getEdgeKey defined above and reused

/**
 * Get the complete boundary loop starting from a single boundary vertex
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} startVertexIndex - Index of a vertex known to be on boundary
 * @param {Array} indices - Face indices array
 * @returns {Array} Array of vertex indices forming the boundary loop
 */
export function getBoundaryLoop(geometry, startVertexIndex, indices) {
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology || !topology.boundaryAdjacency.has(startVertexIndex)) return [];

  const boundaryLoop = [startVertexIndex];
  let current = startVertexIndex;
  let prev = null;
  const safetyLimit = geometry.attributes.position.count * 4;
  let steps = 0;

  while (steps++ < safetyLimit) {
    const neighbors = Array.from(topology.boundaryAdjacency.get(current) || []);
    let next = null;
    for (const n of neighbors) {
      if (n === prev) continue;
      next = n;
      break;
    }
    if (next == null) break;
    if (next === startVertexIndex) break;
    boundaryLoop.push(next);
    prev = current;
    current = next;
  }

  return boundaryLoop;
}

/**
 * Get all vertices connected to a specific vertex
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} vertexIndex - Index of the vertex
 * @param {Array} indices - Face indices array
 * @returns {Array} Array of connected vertex indices
 */
function getConnectedVertices(geometry, vertexIndex, indices) {
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology) return [];
  return Array.from(topology.vertexAdjacency.get(vertexIndex) || []);
}

/**
 * Check if an edge is a boundary edge (used by only one face)
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {number} vertexA - First vertex of the edge
 * @param {number} vertexB - Second vertex of the edge
 * @param {Array} indices - Face indices array
 * @returns {boolean} True if edge is on boundary
 */
function isBoundaryEdge(geometry, vertexA, vertexB, indices) {
  const topology = getOrBuildBoundaryTopology(geometry);
  if (!topology) return false;
  return (topology.edgeUseCount.get(getEdgeKey(vertexA, vertexB)) || 0) === 1;
}

/**
 * Find the topmost vertex in the mesh
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @returns {Object} Object containing {index, position} of the topmost vertex
 */
export function findTopmostVertex(geometry) {
  const positions = geometry.attributes.position.array;
  const vertexCount = positions.length / 3;

  let maxY = -Infinity;
  let topmostIndex = 0;

  for (let i = 0; i < vertexCount; i++) {
    const y = positions[i * 3 + 1];
    if (y > maxY) {
      maxY = y;
      topmostIndex = i;
    }
  }

  return {
    index: topmostIndex,
    position: new THREE.Vector3(
      positions[topmostIndex * 3],
      positions[topmostIndex * 3 + 1],
      positions[topmostIndex * 3 + 2]
    )
  };
}